import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Loader2, Clock, CheckCircle2, XCircle, BarChart2 } from 'lucide-react';
import {
  ServiceGraphOutlineIcon,
  EscalateIcon,
  AlertCircleIcon,
  TeamIcon,
  CalendarIcon,
  WebhookIcon,
  SearchIcon,
  PersonIcon,
  AppsIcon,
  LightningBoltIcon,
  MergeIcon,
  AiIcon,
} from '@pagerduty/continuum.icons';
import { useAsyncTasks } from '@/providers/async-tasks-provider';
import type { CleanupTask, TaskDomain } from './control-panel-dummy-data';

// Per-domain icons. Each Discovery task surfaces with the icon of the
// resource it scans so the panel reads at a glance ("which area is the
// agent looking at right now?"). Falls back to SearchIcon when no
// dedicated icon exists for the domain.
const DOMAIN_ICON: Partial<Record<TaskDomain, React.ElementType>> = {
  services: ServiceGraphOutlineIcon,
  'escalation-policies': EscalateIcon,
  incidents: AlertCircleIcon,
  teams: TeamIcon,
  schedules: CalendarIcon,
  integrations: WebhookIcon,
  discovery: SearchIcon,
  users: PersonIcon,
  'business-services': AppsIcon,
  'event-orchestrations': LightningBoltIcon,
  'incident-workflows': MergeIcon,
  aiops: AiIcon,
  final_report: BarChart2,
};

const DomainIcon: React.FC<{ domain: TaskDomain }> = ({ domain }) => {
  const Icon = DOMAIN_ICON[domain];
  if (!Icon) return null;
  if (domain === 'final_report') {
    return <BarChart2 size={14} color="#718096" aria-label="final report" />;
  }
  return <Icon size="sm" variant="subtle" aria-label={domain} />;
};

const StatusIcon: React.FC<{ status: CleanupTask['status'] }> = ({ status }) => {
  const props = { size: 14 };

  if (status === 'running') {
    return (
      <Box
        as="span"
        display="inline-flex"
        sx={{
          '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
          animation: 'spin 1s linear infinite',
        }}
        color="blue.500"
      >
        <Loader2 {...props} />
      </Box>
    );
  }
  if (status === 'pending') return <Box as="span" color="gray.400"><Clock {...props} /></Box>;
  if (status === 'done') return <Box as="span" color="green.500"><CheckCircle2 {...props} /></Box>;
  return <Box as="span" color="red.500"><XCircle {...props} /></Box>;
};

export const TasksSection: React.FC = () => {
  const { tasks } = useAsyncTasks();

  return (
    <Box flex={1} overflowY="auto">
      <Heading size="sm" color="gray.700" mb={3}>
        Tasks
      </Heading>

      {tasks.length === 0 && (
        <Text fontSize="xs" color="gray.400">No tasks yet.</Text>
      )}

      <VStack align="stretch" spacing={2}>
        {tasks.map((task) => (
          <Box
            key={task.id}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={2}
          >
            <Flex gap={2} align="flex-start">
              <Box pt="1px" flexShrink={0}>
                <StatusIcon status={task.status} />
              </Box>
              <Box flex={1} minW={0}>
                <Flex align="center" gap={1.5} mb={0.5}>
                  {task.domain === 'final_report' && <DomainIcon domain={task.domain} />}
                  <Text fontSize="xs" fontWeight="medium" color="gray.800" flex={1} noOfLines={1}>
                    {task.name}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
